import { Injectable } from '@angular/core';
import { delay, from, map, Observable, switchMap } from 'rxjs';
import { DocumentsFacade, DocumentsFilter } from './documents.facade';
import { DocumentResourceV4 } from '@models/resources/document.model';
import { DEMO_DOCUMENTS_V4 } from '@data/fixtures/documents-v4.fixtures';
import { environment } from '../../../../environments/environment';
import {
  dummyError,
  dummyResponse,
  shouldSimulateError,
} from '@data/dummy-response.util';

const PLACEHOLDER_PDF_PATH = 'assets/demo/placeholder.pdf';

/**
 * V4-Dummy für Kundendokumente. Liest aus `DEMO_DOCUMENTS_V4` (bereits
 * customer-visible-only — BE-Pendant erledigt das Visibility-Filtering
 * serverseitig). Filter (search + classification) greift client-seitig.
 */
@Injectable()
export class DummyDocumentsFacade implements DocumentsFacade {
  list(filter?: DocumentsFilter): Observable<DocumentResourceV4[]> {
    const filtered = applyFilter(DEMO_DOCUMENTS_V4, filter);
    return dummyResponse('customer.documents', filtered);
  }

  download(_id: number): Observable<Blob> {
    if (shouldSimulateError('customer.documents')) {
      return dummyError('customer.documents');
    }
    return from(fetch(PLACEHOLDER_PDF_PATH)).pipe(
      switchMap((response) => {
        if (!response.ok) {
          throw new Error(
            `Placeholder PDF missing at ${PLACEHOLDER_PDF_PATH} (HTTP ${response.status}).`,
          );
        }
        return from(response.blob());
      }),
      map((blob) => (blob.type ? blob : new Blob([blob], { type: 'application/pdf' }))),
      delay(environment.dummyLatencyMs),
    );
  }
}

function applyFilter(
  docs: DocumentResourceV4[],
  filter: DocumentsFilter | undefined,
): DocumentResourceV4[] {
  if (!filter) return docs;
  const search = filter.search?.trim().toLowerCase();
  const classification = filter.classification;
  return docs.filter((d) => {
    if (classification && classification !== 'all' && d.type !== classification) {
      return false;
    }
    if (search) {
      const haystack = `${d.title} ${d.filename} ${d.created_at}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}
